/* eslint-disable max-lines */

import Rater from 'react-rater';
import 'react-rater/lib/react-rater.css';
import React from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import {MuiThemeProvider} from '@material-ui/core/styles';
import Comments from './Comments';
import trackAnalytics from '../../../utils/gaia/trackAnalytics';
import experiments from '../../../utils/gaia/experiments';

import DeleteReviewConfirmationDialog from './DeleteReviewConfirmationDialog';
import theme from '../../../theme';
import {SUMMER} from '../../../colors';

/**
 * ReviewForm component for entering/editing a review for a hike.
 * Material-UI Dialog.
 *
 *
 * @param {number}           props.initialStars - The star rating to save after the user clicks the
 *                                                stars and the form shows initially.
 * @param {array?}           props.reviews - A list of reviews dicts.
 * @param {number}           props.regionId - The id of the park or hike.
 * @param {number?}          props.userId - The user id for the current user.
 *
 * @return {React.Component}
 */
export default class ReviewForm extends React.Component {
    constructor(props) {
        super(props);
        const currentUserReview = this.currentUserReview(props.reviews);
        this.state = {
            confirmDeleteOpen: false,
            didEdit: false,
            editedReview: {
                stars: currentUserReview.stars,
                notes: currentUserReview.notes,
            },
            reviews: props.reviews,
            saveToastOpen: false,
            submittingReview: false,
        };
    }

    currentUserReview(reviews) {
        for (const review of reviews) {
            if (review.user === this.props.userId) {
                return {
                    id: review.unique_id,
                    notes: review.notes || '',
                    stars: parseInt(review.stars),
                };
            }
        }
        return this.blankReview();
    }

    blankReview() {
        return {
            id: null,
            notes: '',
            stars: 0,
        };
    }

    getApiEndPoint() {
        return '/api/trip/review/' + this.props.regionId + '/';
    }

    postApiEndPoint() {
        return '/api/trip/report/' + this.props.regionId + '/';
    }

    async updateComments() {
        try {
            const response = await fetch(this.getApiEndPoint());
            if (!response.ok) {
                throw Error(response.statusText);
            }
            const json = await response.json();
            this.setState({reviews: json, editedReview: this.currentUserReview(json)});
        } catch (error) {
            console.error(error);
        }
    }

    componentDidUpdate() {
        // This happens when there is a rate_it parameter in the querystring.
        // This parameter gets set when clicking from email, or from login redirect.
        // The star rating gets saved, and the view reveals a text box.
        if (this.props.initialRating > 0 && !this.state.savedInitialInfo) {
            const notes = this.props.initialNotes || '';
            this.setState({
                savedInitialInfo: true,
                editedReview: {
                    stars: this.props.initialRating,
                    notes: notes,
                }
            });
            if (this.props.userId) {
                this.saveRating(this.props.initialRating, notes);
            }
        }
    }

    // called when the user types into the TextField
    handleReviewEdit(event) {
        if (!this.state.didEdit) {
            trackAnalytics('Review Start Typing', {
                category: 'Hike',
            });
        }
        const editedReview = this.state.editedReview;
        editedReview.notes = event.target.value;
        this.setState({
            didEdit: true,
            editedReview: editedReview,
        });
    }

    // called when the user presses Save - posts to Server and updates page
    saveReview() {
        if (this.props.userId === 'None') {
            // eslint-disable-next-line max-len
            window.location.href = `/register/?next=${window.location.pathname}%3Freview_text=${this.state.editedReview.notes}%26rate_it=${this.state.editedReview.stars}`;
            return;
        }

        this.setState({
            showRatingError: (this.state.editedReview.stars <= 0),
            submittingReview: true
        });

        if ((this.state.editedReview.stars <= 0)) {
            trackAnalytics('Review Error', {
                category: 'Hike',
                rating: this.state.editedReview.stars,
                reviewLength: this.state.editedReview.notes.length,
            });
            this.setState({
                submittingReview: false,
            });
            return;
        }
        this.saveRating(this.state.editedReview.stars, this.state.editedReview.notes);
    }

    async saveRating(stars, notes) {
        this.setState({
            editedReview: {stars: stars, notes: notes}
        });
        const body_data = {
            stars: stars,
            report_unique_id: this.currentUserReview(this.state.reviews).id,
            notes: notes
        };

        const response = await fetch(this.postApiEndPoint(), {
            body: JSON.stringify(body_data),
            method: 'POST',
            credentials: 'same-origin'
        });
        const myJson = await response.json();
        if (myJson.success) {
            experiments.goal('review save');
            trackAnalytics('Review Save', {
                category: 'Hike',
                rating: this.state.editedReview.stars,
                reviewLength: this.state.editedReview.notes.length,
            });
            this.setState({
                saveToastOpen: true,
                showRatingError: false,
                submittingReview: false,
            });
            this.updateComments();
            sleep(2000).then(() => {
                this.setState({saveToastOpen: false});
            });
        } else if (myJson.errors) {
            trackAnalytics('Review Error', {
                category: 'Hike',
                rating: this.state.editedReview.stars,
                reviewLength: this.state.editedReview.notes.length,
                serverError: true,
            });
            this.setState({
                submittingReview: false,
            });
        }

    }

    // Show a modal confirmation dialog
    confirmDelete() {
        this.setState({confirmDeleteOpen: true});
    }

    deleteReview() {
        const body_data = {
        };
        fetch(this.postApiEndPoint(),
            {
                body: JSON.stringify(body_data),
                method: 'DELETE',
                credentials: 'same-origin'
            }
        )
            .then(response => {
                return response.json();
            })
            .then(myJson => {
                if (myJson.success) {
                    this.setState({
                        comments: [],
                        confirmDeleteOpen: false,
                        editedReview: this.blankReview(),
                        showRatingError: false,
                    });
                    this.updateComments();
                    trackAnalytics('Review Delete', {
                        category: 'Hike',
                        rating: this.state.editedReview.stars,
                        reviewLength: this.state.editedReview.notes.length,
                    });
                } else if (myJson.errors) {
                    trackAnalytics('Review Delete Error', {
                        category: 'Hike',
                        rating: this.state.editedReview.stars,
                        reviewLength: this.state.editedReview.notes.length,
                    });
                }
            });
    }

    render() {
        let title = 'Rate this Hike';
        let buttonTitle = 'Submit Trip Report';
        // These titles shows when the user is logged out and clicks a star.
        if (this.state.editedReview.stars > 0) {
            title = 'Add a Report';
            buttonTitle = 'Submit Rating';
        }
        const currentUserReview = this.currentUserReview(this.state.reviews);
        if (currentUserReview) {
            if (this.state.editedReview.notes.length) {
                title = 'Edit Your Report';
            } else {
                title = 'Add a Report';
            }
            buttonTitle = 'Save';
        }
        let saveToastMessage = 'Your trip report has been saved.';
        if (this.state.editedReview.notes.length === 0) {
            saveToastMessage = 'Your rating has been saved.';
        }
        return (
            <MuiThemeProvider theme={theme}>
                <div>
                    <h4 className='hk-info-header'>
                        {title}
                    </h4>
                    {this.state.showRatingError && (
                        <div style={{color: '#CF3A20', marginBottom: 8}}>
                            Please select a star rating to submit your review.
                        </div>
                    )}
                    {this.state.editedReview.stars > 0 && (
                        <TextField
                            variant='outlined'
                            margin='dense'
                            fullWidth
                            id="descriptionTextField"
                            onChange={e => this.handleReviewEdit(e)}
                            // eslint-disable-next-line max-len
                            placeholder="Write a trip report. Focus on time-based information, like 'The wildflowers were blooming' or 'It was muddy from the previous week's rain.' For non-time based information, edit the description instead."
                            rows={4}
                            multiline={true}
                            rowsMax={5}
                            value={this.state.editedReview.notes}
                        />
                    )}

                    <div style={{
                        display: 'inline-block',
                        marginRight: theme.spacing(1),
                        top: 10,
                        fontSize: 20,
                        position: 'relative'
                    }}>
                        <Rater
                            total={5}
                            rating={this.state.editedReview.stars}
                            interactive={true}
                            onRate={e => {
                                if (e.type === 'click') {
                                    this.saveRating(e.rating, this.state.editedReview.notes);
                                    trackAnalytics('Review Star Rating', {
                                        category: 'Hike',
                                        rating: e.rating,
                                    });
                                }
                            }}
                        />
                    </div>

                    {this.state.editedReview.stars > 0 && (
                        <Button
                            color='primary'
                            disabled={this.state.submittingReview}
                            label={buttonTitle}
                            onClick={() => this.saveReview()}
                            style={{
                                backgroundColor: '#64D44C',
                                color: 'white',
                                marginTop: theme.spacing(2),
                                minWidth: 120
                            }}
                            variant="text"
                        >{
                                this.state.submittingReview ?
                                    <CircularProgress size={21} style={{color: 'white'}} /> :
                                    buttonTitle
                            }</Button>
                    )}
                    {currentUserReview.id && (
                        <Button
                            color='secondary'
                            disabled={!currentUserReview.id}
                            onClick={() => this.confirmDelete()}
                            style={{marginTop: theme.spacing(2)}}
                            variant="text"
                        >Delete</Button>
                    )}
                    {this.state.saveToastOpen &&
                        <div
                            className="alert alert-success"
                            style={{marginTop: theme.spacing(1.5)}}
                        >
                            {saveToastMessage}
                        </div>
                    }
                    <div style={{marginTop: theme.spacing(3)}}>
                        {this.state.reviews.length === 0 && this.state.editedReview.stars === 0 && (
                            <h3 style={{color: SUMMER}}>Be the first to rate this hike!</h3>
                        )}
                        <Comments comments={this.state.reviews} />
                    </div>
                    <DeleteReviewConfirmationDialog
                        open={this.state.confirmDeleteOpen}
                        handleDelete={() => this.deleteReview()}
                    />
                </div>
            </MuiThemeProvider>
        );
    }
}


// sleep time expects milliseconds
function sleep(time) {
    return new Promise(resolve => setTimeout(resolve, time));
}
